import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

type AppRole = 'owner' | 'admin' | 'supervisor' | 'operator';

/**
 * Props for {@link ProtectedRoute}.
 *
 * @property children - The protected subtree to render once the user is
 *   authenticated and (optionally) authorized.
 * @property requiredRoles - Optional whitelist of roles allowed to render the
 *   children. If omitted, any authenticated user is granted access.
 */
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
}

/**
 * Authentication and role-based authorization guard for React Router routes.
 *
 * Behaviour:
 *  - While the auth context is bootstrapping, renders a spinner so children
 *    never see a half-initialized session.
 *  - If there is no authenticated user, redirects to `/login` via
 *    `<Navigate replace />` (no history entry pollution).
 *  - If `requiredRoles` is provided and the user's role is not included,
 *    renders an inline "Access Denied" panel instead of redirecting — this
 *    keeps the sidebar/header visible so the user can navigate elsewhere.
 */
export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user, role, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Tenant super-admins (owner + admin) can access every module/route.
  const isPrivileged = role === 'owner' || role === 'admin';

  if (requiredRoles && role && !isPrivileged && !requiredRoles.includes(role)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center space-y-2">
          <h2 className="text-xl font-bold text-destructive">Access Denied</h2>
          <p className="text-sm text-muted-foreground">
            Your role ({role}) does not have permissions to access this module.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
